import type { DroneTransport, FlightMode, GpsFixType, MissionState, Role, UserStatus } from './types';
import { ROLE_LABELS } from './roles';
import { safeString } from './math';

/* ------------------------------------------------------------------ *
 * 数值
 * ------------------------------------------------------------------ */

export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--';
  return value.toFixed(digits);
}

export function formatPercent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--';
  return `${value.toFixed(digits)}%`;
}

/** 距离：小于 1000 米用米，否则用公里。 */
export function formatDistance(meters: number | null | undefined): string {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return '--';
  if (Math.abs(meters) < 1000) return `${meters.toFixed(0)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

export function formatSpeed(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '--';
  return `${ms.toFixed(1)} m/s`;
}

export function formatAltitude(meters: number | null | undefined): string {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return '--';
  return `${meters.toFixed(1)} m`;
}

export function formatDegrees(deg: number | null | undefined, digits = 1): string {
  if (deg === null || deg === undefined || !Number.isFinite(deg)) return '--';
  return `${deg.toFixed(digits)}°`;
}

export function formatCoord(lat: number, lng: number, digits = 6): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '--';
  const latHemisphere = lat >= 0 ? 'N' : 'S';
  const lngHemisphere = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(digits)}°${latHemisphere}, ${Math.abs(lng).toFixed(digits)}°${lngHemisphere}`;
}

/** 把秒数格式化为「1 小时 23 分」。 */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) return '--';
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return `${hours} 小时 ${minutes} 分`;
  if (minutes > 0) return `${minutes} 分 ${rest} 秒`;
  return `${rest} 秒`;
}

/** 紧凑时长：「1h23m」「23m05s」「12s」。 */
export function formatDurationCompact(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) return '--';
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) return `${hours}h${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m${String(rest).padStart(2, '0')}s`;
  return `${rest}s`;
}

/** 相对时间：「刚刚」「12 秒前」「3 分钟前」。 */
export function formatRelativeTime(epochMs: number | null | undefined, now = Date.now()): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '从未';
  const diff = now - epochMs;
  if (diff < 0) return '刚刚';
  if (diff < 1000) return '刚刚';
  if (diff < 60_000) return `${Math.floor(diff / 1000)} 秒前`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`;
  return `${Math.floor(diff / 86_400_000)} 天前`;
}

/** 绝对时间：`2026-09-19 10:48:33`。 */
export function formatDateTime(epochMsOrIso: number | string | null | undefined): string {
  if (epochMsOrIso === null || epochMsOrIso === undefined) return '--';
  const date = typeof epochMsOrIso === 'number' ? new Date(epochMsOrIso) : new Date(epochMsOrIso);
  if (Number.isNaN(date.getTime())) return '--';
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function formatTimeOnly(epochMsOrIso: number | string | null | undefined): string {
  if (epochMsOrIso === null || epochMsOrIso === undefined) return '--';
  const date = typeof epochMsOrIso === 'number' ? new Date(epochMsOrIso) : new Date(epochMsOrIso);
  if (Number.isNaN(date.getTime())) return '--';
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/* ------------------------------------------------------------------ *
 * 枚举中文标签
 * ------------------------------------------------------------------ */

export const FLIGHT_MODE_LABELS: Readonly<Record<FlightMode, string>> = {
  manual: '手动',
  stabilize: '增稳',
  loiter: '悬停',
  auto: '自动航线',
  rtl: '自动返航',
  land: '自动降落',
  offboard: '外部控制',
};

export const MISSION_STATE_LABELS: Readonly<Record<MissionState, string>> = {
  idle: '空闲',
  uploading: '上传中',
  ready: '待执行',
  executing: '执行中',
  paused: '已暂停',
  completed: '已完成',
  failed: '执行失败',
};

export const GPS_FIX_LABELS: Readonly<Record<GpsFixType, string>> = {
  none: '无定位',
  '2d': '2D 定位',
  '3d': '3D 定位',
  'rtk-float': 'RTK 浮动解',
  'rtk-fixed': 'RTK 固定解',
};

export const TRANSPORT_LABELS: Readonly<Record<DroneTransport, string>> = {
  ws: 'WebSocket',
  poll: 'REST 轮询',
};

export const USER_STATUS_LABELS: Readonly<Record<UserStatus, string>> = {
  active: '已启用',
  suspended: '已停用',
};

export function roleLabel(role: Role | null | undefined): string {
  if (!role) return '未知';
  return ROLE_LABELS[role] ?? safeString(role);
}

export function flightModeLabel(mode: FlightMode | null | undefined): string {
  if (!mode) return '--';
  return FLIGHT_MODE_LABELS[mode] ?? mode;
}

export function missionStateLabel(state: MissionState | null | undefined): string {
  if (!state) return '--';
  return MISSION_STATE_LABELS[state] ?? state;
}

export function gpsFixLabel(fix: GpsFixType | null | undefined): string {
  if (!fix) return '--';
  return GPS_FIX_LABELS[fix] ?? fix;
}

export function userStatusLabel(status: UserStatus | null | undefined): string {
  if (!status) return '--';
  return USER_STATUS_LABELS[status] ?? status;
}

/** 信号质量的文字分级，用于卡片上的可读描述。 */
export function signalQualityLabel(quality: number | null | undefined): string {
  if (quality === null || quality === undefined || !Number.isFinite(quality)) return '未知';
  if (quality >= 80) return '优秀';
  if (quality >= 60) return '良好';
  if (quality >= 40) return '一般';
  if (quality >= 20) return '较差';
  return '极差';
}

/** 电量文字分级。 */
export function batteryLabel(percent: number | null | undefined): string {
  if (percent === null || percent === undefined || !Number.isFinite(percent)) return '未知';
  if (percent >= 60) return '充足';
  if (percent >= 35) return '正常';
  if (percent >= 25) return '偏低';
  if (percent >= 15) return '不足';
  return '严重不足';
}

/** RSSI(dBm) → 0-100 的粗略映射，仅在报文未提供 quality 时兜底。 */
export function rssiToQuality(rssiDbm: number): number {
  if (!Number.isFinite(rssiDbm)) return 0;
  const clamped = Math.min(-30, Math.max(-100, rssiDbm));
  return Math.round(((clamped + 100) / 70) * 100);
}
